import { gql } from '@apollo/client';

// ALL PRODUCTS -- Only necessary data what's in products__item
export const filterQuery = gql`
  query AllProducts(
    $type: [hasuradb_product_types_enum!]
    $tags: [String!]
    $targetGroups: [hasuradb_target_groups_enum!]
    $months: [hasuradb_months_enum!]
    $companies: [uuid!]
    $search: String_comparison_exp
    $orderBy: order_by!
    $offset: Int
    $limit: Int
    $cities: [uuid!]
    $certificates: [String!]
    $accessible: Boolean
  ) {
    product(
      where: {
        type: { _in: $type }
        _or: [
          { productInformations: { name: { _ilike: $search } } }
          { productInformations: { description: { _ilike: $search } } }
          { company: { businessName: { _ilike: $search } } }
        ]
        productAvailableMonths: { month: { _in: $months } }
        productTargetGroups: { targetGroupId: { _in: $targetGroups } }
        postalAddresses: { postalArea: { city: { id: { _in: $cities } } } }
        accessible: { _eq: $accessible }
      }
      order_by: { updatedAt: $orderBy }
      limit: $limit
      offset: $offset
    ) {
      id
      type
      company {
        businessEntity {
          businessId
        }
        businessName
        id
      }
      postalAddresses {
        location
        city
      }
      productAvailableMonths {
        month
      }
      productCertificates {
        certificateDetails {
          description
          name
          logoUrl
          websiteUrl
        }
        certificate
      }
      productInformations {
        description
        name
        language
      }
      productImages {
        altText
        thumbnailUrl
      }
      productTags {
        tag
      }
      productTargetGroups {
        targetGroupId
      }
      accessible
    }
  }
`;

// $type: [hasuradb_product_types_enum!]!
// $tags: [String!]!
// $targetGroups: [hasuradb_target_groups_enum!]!
// $cities: [uuid!]!
// $months: [hasuradb_months_enum!]!

const filters = (variables) => {
  const baseFilters = {
    search: `
      _or: [
        { productInformations: { name: { _ilike: $search } } }
        { productInformations: { description: { _ilike: $search } } }
        { company: { businessName: { _ilike: $search } } }
      ]
      `,
    cities: `postalAddresses: { postalArea: { city: { id: { _in: $cities } } } }`,
    type: `type: { _in: $type }`,
    tags: `productTags: { tag: { _in: $tags } }`,
    months: `productAvailableMonths: { month: { _in: $months } }`,
    targetGroups: `productTargetGroups: { targetGroupId: { _in: $targetGroups } }`,
    accessible: `accessible: { _eq: $accessible }`,
    certificates: `productCertificates: { certificate: { _in: $certificates } }`,
  };

  return Object.keys(variables)
    .filter((key) => variables[key] !== undefined)
    .map((key) => baseFilters[key])
    .join('\n');
};

const params = (variables = {}) =>
  Object.entries({
    accessible: 'Boolean!',
    certificates: '[String!]!',
    cities: '[uuid!]!',
    limit: 'Int!',
    months: '[hasuradb_months_enum!]!',
    offset: 'Int!',
    orderBy: 'order_by!',
    search: 'String!',
    tags: '[String!]!',
    targetGroups: '[hasuradb_target_groups_enum!]!',
    type: '[hasuradb_product_types_enum!]!',
  })
    .filter(([key]) => Object.keys(variables).includes(key))
    .map(([key, value]) => `$${key}: ${value}`)
    .join(', ');

export const generateQuery = (variables) => {
  const filteredVariables = Object.keys(variables).reduce(
    (output, key) => ({
      ...output,
      ...(variables[key] !== undefined && variables[key] !== null ? { [key]: variables[key] } : {}),
    }),
    {},
  );

  const productParams = params(filteredVariables);

  const query = `
    query getProducts${productParams.length > 0 ? `(${productParams}) ` : ''} {
      product(
        where: {
          ${filters(filteredVariables).trim()}
        }
        ${filteredVariables.orderBy !== undefined ? 'order_by: { updatedAt: $orderBy }' : ''}
        ${filteredVariables.limit !== undefined ? 'limit: $limit' : ''}
        ${filteredVariables.offset !== undefined ? 'offset: $offset' : ''}
      ) {
        id
        type
        company {
          businessEntity {
            businessId
          }
          businessName
          id
        }
        postalAddresses {
          location
          city
        }
        productAvailableMonths {
          month
        }
        productCertificates {
          certificateDetails {
            description
            name
            logoUrl
            websiteUrl
          }
          certificate
        }
        productInformations {
          description
          name
          language
        }
        productImages {
          altText
          thumbnailUrl
        }
        productTags {
          tag
        }
        productTargetGroups {
          targetGroupId
        }
        accessible
      }
    }
  `;

  return gql`
    ${query}
  `;
};

const t = gql`
  query getCitiesInRegions {
    region {
      region
      cities {
        city
        id
        __typename
      }
      __typename
    }
  }
`;

export const DataHubQuery = gql`
  query getProducts($orderBy: order_by!, $offset: Int!, $limit: Int!, $search: String!) {
    product(
      where: {
        # type: { _in: $type }
        # productTags: { tag: { _in: $tags } }
        # productTargetGroups: { targetGroupId: { _in: $targetGroups } }
        # postalAddresses: { postalArea: { city: { id: { _in: $cities } } } }
        # productAvailableMonths: { month: { _in: $months } }
        _or: [
          { productInformations: { name: { _ilike: $search } } }
          { productInformations: { description: { _ilike: $search } } }
          { company: { businessName: { _ilike: $search } } }
        ]
      }
      order_by: { updatedAt: $orderBy }
      limit: $limit
      offset: $offset
    ) {
      id
      accessible
      productImages {
        largeUrl
        originalUrl
        thumbnailUrl
        altText
        copyright
        filename
        __typename
      }
      productInformations {
        language
        name
        description
        __typename
      }
      postalAddresses {
        city
        __typename
      }
      company {
        businessName
        __typename
      }
      updatedAt
      type
      __typename
    }
  }
`;

export const sustainableFilterQuery = gql`
  query AllProductsSustainable(
    $type: [hasuradb_product_types_enum!]
    $tags: [String!]
    $targetGroups: [hasuradb_target_groups_enum!]
    $months: [hasuradb_months_enum!]
    $companies: [uuid!]
    $search: String_comparison_exp
    $orderBy: order_by!
    $offset: Int
    $limit: Int
    $cities: [uuid!]
    $certificates: [String!]
    $accessible: Boolean
  ) {
    product(
      where: {
        type: { _in: $type }
        _or: [
          { productInformations: { name: $search } }
          { productInformations: { description: $search } }
          { company: { businessName: $search } }
        ]
        productAvailableMonths: { month: { _in: $months } }
        productTargetGroups: { targetGroupId: { _in: $targetGroups } }
        postalAddresses: { postalArea: { city: { id: { _in: $cities } } } }
        productCertificates: { certificate: { _in: $certificates } }
        accessible: { _eq: $accessible }
      }
      order_by: { updatedAt: $orderBy }
      limit: $limit
      offset: $offset
    ) {
      id
      type
      company {
        businessEntity {
          businessId
        }
        businessName
        id
      }
      postalAddresses {
        location
        city
      }
      productAvailableMonths {
        month
      }
      productInformations {
        description
        name
        language
      }
      productCertificates {
        certificateDetails {
          description
          name
          logoUrl
          websiteUrl
        }
        certificate
      }
      productImages {
        altText
        thumbnailUrl
      }
      productTags {
        tag
      }
      productTargetGroups {
        targetGroupId
      }
      accessible
    }
  }
`;

// Only ID's of all products that are available. Also applied with filters. This one is used for pagination and showing amount of results
export const totalProductsQuery = gql`
  query TotalProducts(
    $type: [hasuradb_product_types_enum!]
    $tags: [String!]
    $targetGroups: [hasuradb_target_groups_enum!]
    $months: [hasuradb_months_enum!]
    $search: String
    $cities: [uuid!]
    $accessible: Boolean
  ) {
    product(
      where: {
        type: { _in: $type }
        _and: [
          {
            _or: [
              { productInformations: { name: { _ilike: $search } } }
              { productInformations: { description: { _ilike: $search } } }
              { company: { businessName: { _ilike: $search } } }
            ]
          }
          { _or: [{ _not: { productTags: {} } }, { productTags: { tag: { _in: $tags } } }] }
        ]
        productAvailableMonths: { month: { _in: $months } }
        productTargetGroups: { targetGroupId: { _in: $targetGroups } }
        postalAddresses: { postalArea: { city: { id: { _in: $cities } } } }
        accessible: { _eq: $accessible }
      }
    ) {
      id
    }
  }
`;

export const sustainableTotalProductsQuery = gql`
  query TotalProductsSustainable(
    $type: [hasuradb_product_types_enum!]
    $tags: [String!]
    $targetGroups: [hasuradb_target_groups_enum!]
    $months: [hasuradb_months_enum!]
    $search: String
    $cities: [uuid!]
    $certificates: [String!]
    $accessible: Boolean
  ) {
    product(
      where: {
        type: { _in: $type }
        _and: [
          {
            _or: [
              { productInformations: { name: { _ilike: $search } } }
              { productInformations: { description: { _ilike: $search } } }
              { company: { businessName: { _ilike: $search } } }
            ]
          }
          { _or: [{ _not: { productTags: {} } }, { productTags: { tag: { _in: $tags } } }] }
        ]
        productAvailableMonths: { month: { _in: $months } }
        productTargetGroups: { targetGroupId: { _in: $targetGroups } }
        postalAddresses: { postalArea: { city: { id: { _in: $cities } } } }
        productCertificates: { certificate: { _in: $certificates } }
        accessible: { _eq: $accessible }
      }
    ) {
      id
    }
  }
`;

// this query get's you all the cities
export const regionQuery = gql`
  query getCitiesInRegions {
    region {
      cities {
        city
        id
      }
    }
  }
`;

export const popupQuery = gql`
  query popupProduct($id: uuid) {
    product(where: { id: { _eq: $id } }) {
      id
      webshopUrlPrimary
      urlPrimary
      type
      company {
        businessEntity {
          businessId
        }
        businessName
        officialName
        socialMediaLinks {
          linkType
          url
        }
        description
        logoThumbnailUrl
        logoUrl
        id
      }
      duration
      durationType
      openingHours {
        open
        openFrom
        openTo
        weekday
      }
      postalAddresses {
        location
        postalCode
        streetName
        city
      }
      productAvailableMonths {
        month
      }
      productInformations {
        description
        language
        name
      }
      productCertificates {
        certificateDetails {
          description
          name
          logoUrl
          websiteUrl
        }
        certificate
      }
      productImages {
        copyright
        filename
        altText
        largeUrl
        originalUrl
        thumbnailUrl
        coverPhoto
        orientation
      }
      productPricings {
        toPrice
        fromPrice
      }
      productTags {
        tag
      }
      productTargetGroups {
        targetGroupId
      }
      productVideos {
        title
        url
      }
      accessible
    }
  }
`;
